import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useIntl } from '@edx/frontend-platform/i18n';
import { getConfig } from '@edx/frontend-platform';

import {
  ActionRow,
  IconButton,
  Icon,
  Spinner,
} from '@openedx/paragon';
import { Close, ThumbUpOutline, ThumbDownOffAlt } from '@openedx/paragon/icons';

import './index.scss';
import messages from './messages';
import FeedbackDetailsModal from './FeedbackDetailsModal';
import useFeedbackWidget from './useFeedbackWidget';

const FeedbackWidget = ({
  courseId, courseLanguage, translationLanguage, unitId, userId,
}) => {
  const { formatMessage } = useIntl();
  const ref = useRef(null);
  const [elemReady, setElemReady] = useState(false);
  const [isFeedbackDetailsModalOpen, setIsFeedbackDetailsModalOpen] = useState(false);
  const [currentFeedbackValue, setCurrentFeedbackValue] = useState(false);
  const {
    closeFeedbackWidget,
    isLoading,
    showFeedbackWidget,
    showGratitudeText,
    onSendPositiveFeedback,
    onSendNegativeFeedback,
  } = useFeedbackWidget({
    courseId,
    courseLanguage,
    translationLanguage,
    unitId,
    userId,
  });

  useEffect(() => {
    const domNode = document.getElementById(
      'whole-course-translation-feedback-widget',
    );
    if (ref.current && domNode) {
      while (domNode.firstChild) {
        domNode.removeChild(domNode.lastChild);
      }
      domNode.appendChild(ref.current);
      setElemReady(true);
    }
  }, [ref.current]);

  if (!getConfig().AI_TRANSLATIONS_URL) {
    return null;
  }

  const onThumbsUpClick = () => {
    setCurrentFeedbackValue(true);
    setIsFeedbackDetailsModalOpen(true);
  };

  const onThumbsDownClick = () => {
    setCurrentFeedbackValue(false);
    setIsFeedbackDetailsModalOpen(true);
  };

  const onCloseFeedbackDetailsModal = () => {
    setIsFeedbackDetailsModalOpen(false);
  };

  return (
    <div
      ref={ref}
      className={
        elemReady ? 'sequence-container d-inline-flex flex-row w-100' : 'd-none'
      }
    >
      {showFeedbackWidget || showGratitudeText || isLoading ? (
        <div className="sequence w-100">
          {showFeedbackWidget && (
            isLoading ? (
              <div className="mt-1 mb-1">
                <ActionRow className="m-2 justify-content-center">
                  <Spinner animation="border" variant="primary" className="mr-3" screenReaderText="loading" />
                </ActionRow>
              </div>
            ) : (
              <div className="ml-4 mr-2">
                <ActionRow>
                  {formatMessage(messages.rateTranslationText)}
                  <ActionRow.Spacer />
                  <div>
                    <IconButton
                      src={ThumbUpOutline}
                      iconAs={Icon}
                      alt="positive-feedback"
                      onClick={onThumbsUpClick}
                      variant="secondary"
                      className="m-1"
                      id="positive-feedback-button"
                    />
                    <IconButton
                      src={ThumbDownOffAlt}
                      iconAs={Icon}
                      alt="negative-feedback"
                      onClick={onThumbsDownClick}
                      variant="secondary"
                      className="mr-2"
                      id="negative-feedback-button"
                    />
                  </div>
                  <div className="mb-1 text-light action-row-divider">|</div>
                  <div>
                    <IconButton
                      src={Close}
                      iconAs={Icon}
                      alt="close-feedback"
                      onClick={closeFeedbackWidget}
                      variant="secondary"
                      className="ml-1 mr-2 float-right"
                      id="close-feedback-button"
                    />
                  </div>
                </ActionRow>
              </div>
            )
          )}
          {showGratitudeText && (
            <div className="ml-4 mr-4">
              <ActionRow className="m-2 justify-content-center">
                {formatMessage(messages.gratitudeText)}
              </ActionRow>
            </div>
          )}
          <FeedbackDetailsModal
            isOpen={isFeedbackDetailsModalOpen}
            close={onCloseFeedbackDetailsModal}
            currentFeedbackValue={currentFeedbackValue}
            onSendPositiveFeedback={onSendPositiveFeedback}
            onSendNegativeFeedback={onSendNegativeFeedback}
          />
        </div>
      ) : null}
    </div>
  );
};

FeedbackWidget.propTypes = {
  courseId: PropTypes.string.isRequired,
  courseLanguage: PropTypes.string.isRequired,
  translationLanguage: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  unitId: PropTypes.string.isRequired,
};

FeedbackWidget.defaultProps = {};

export default FeedbackWidget;
